<template>
  <base-modal
    ref="modalRef"
    :style="cssVars"
    mode="multi"
    :title="props.title || 'Select Items'"
    :primary-color="props.primaryColor"
    :clearable="props.clearable"
    :clear-text="props.clearText || 'Clear'"
    :clear-aria-label="props.clearAriaLabel"
    :width="props.width || 'xl'"
    :height="props.height"
    :show-find="props.showFind"
    :find-placeholder="props.findPlaceholder"
    :auto-focus-find="props.autoFocusFind"
    :show-select-all="props.showSelectAll"
    :show-badge-count="props.showBadgeCount"
    :show-archived-toggle="props.showArchivedToggle"
    :include-archived="props.includeArchived"
    :show-none-option="props.showNoneOption"
    :none-option-text="props.noneOptionText"
    :none-selected="props.noneSelected"
    :show-row2="props.showRow2"
    :show-footer-controls="props.showFooterControls"
    :show-footer="props.showFooter"
    :primary-action-text="props.primaryActionText"
    :secondary-action-text="props.secondaryActionText"
    :hide-secondary-action="props.hideSecondaryAction"
    :loading="props.loading"
    :body-padding="false"
    @show="onShow"
    @hide="onHide"
    @shown="onShown"
    @clear="handleClear"
    @search="handleSearch"
    @select-all="handleSelectAll"
    @save="handleSave"
    @cancel="handleCancel"
    @enter-key="handleEnterKey"
    @focus-first="$emit('focus-first')"
    @focus-last="$emit('focus-last')"
    @focus-body-last="$emit('focus-body-last')"
    @archived-toggle-key="$emit('archived-toggle-key', $event)"
    @none-select-key="$emit('none-select-key', $event)"
    @toggle-archived="$emit('toggle-archived', $event)"
    @select-none="$emit('select-none', $event)"
    @toggle-subtasks="$emit('toggle-subtasks')"
    @create-key="$emit('create-key', $event)"
    @new-key="$emit('new-key', $event)"
    @navigate-left="$emit('navigate-left')"
    @navigate-right="$emit('navigate-right')"
  >
    <!-- Forward header slots -->
    <template v-if="$slots['header-second-row']" #header-second-row>
      <slot name="header-second-row" />
    </template>
    <template v-if="$slots['header-controls']" #header-controls>
      <slot name="header-controls" />
    </template>

    <!-- Forward custom footer controls slot -->
    <template v-if="$slots['footer-custom-controls']" #footer-custom-controls>
      <slot name="footer-custom-controls" />
    </template>

    <!-- Two-pane layout -->
    <div class="two-pane-template tw-flex tw-min-h-0 tw-flex-1">
      <!-- Left pane (available items) -->
      <div
        class="two-pane-template__left tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-border-r tw-border-gray-medium"
        :class="leftPaneClasses"
      >
        <!-- Left pane header (optional) -->
        <div
          v-if="$slots['left-header'] || props.leftHeaderTitle"
          class="two-pane-template__left-header tw-shrink-0 tw-border-b tw-border-gray-medium tw-px-3 tw-py-2"
        >
          <slot name="left-header">
            <span class="tw-text-sm tw-font-medium">{{
              props.leftHeaderTitle
            }}</span>
          </slot>
        </div>

        <!-- Navigation breadcrumbs (optional) -->
        <div
          v-if="$slots.navigation"
          class="two-pane-template__navigation tw-shrink-0 tw-border-b tw-border-gray-medium tw-px-3 tw-py-2"
        >
          <slot name="navigation" />
        </div>

        <!-- Filters (optional) -->
        <div
          v-if="$slots.filters"
          class="two-pane-template__filters tw-shrink-0 tw-border-b tw-border-gray-medium tw-p-3"
        >
          <slot name="filters" />
        </div>

        <!-- Left pane body (scrollable list) -->
        <div
          class="two-pane-template__left-body tw-min-h-0 tw-flex-1 tw-overflow-y-auto"
        >
          <slot
            name="left"
            :search-query="searchQuery"
            :is-loading="props.loading"
          />
        </div>

        <!-- Left pane footer (optional - e.g., toggles, filters) -->
        <div
          v-if="props.showLeftFooter"
          class="two-pane-template__left-footer tw-shrink-0 tw-border-t tw-border-gray-medium"
        >
          <slot name="left-footer" />
        </div>
      </div>

      <!-- Right pane (selected items display) -->
      <div
        class="two-pane-template__right tw-flex tw-min-h-0 tw-flex-1 tw-flex-col"
        :class="rightPaneClasses"
      >
        <!-- Right pane header (customizable) -->
        <div
          v-if="$slots['right-header'] || props.rightHeaderTitle"
          class="two-pane-template__right-header tw-shrink-0 tw-border-b tw-border-gray-medium"
        >
          <slot name="right-header">
            <div
              v-if="props.rightHeaderTitle"
              class="tw-flex tw-size-full tw-items-center tw-justify-center tw-p-3"
            >
              <span class="tw-text-sm tw-font-medium">{{
                props.rightHeaderTitle
              }}</span>
            </div>
            <SelectedItemsHeader v-else :primary-color="props.primaryColor" />
          </slot>
        </div>

        <!-- Right pane body (scrollable selected items) -->
        <div
          class="two-pane-template__right-body tw-min-h-0 tw-flex-1 tw-overflow-y-auto"
        >
          <slot
            name="right"
            :selected-items="props.selectedItems"
            :has-selections="hasSelections"
          />
        </div>

        <!-- Right pane footer (optional - e.g., summary, actions) -->
        <div
          v-if="props.showRightFooter"
          class="two-pane-template__right-footer tw-shrink-0 tw-border-t tw-border-gray-medium"
        >
          <slot name="right-footer" :count="selectedCount" />
        </div>
      </div>
    </div>
  </base-modal>
</template>

<script lang="ts">
export default {
  inheritAttrs: false,
};
</script>

<script setup lang="ts" generic="T = unknown">
import { ref, computed, watch } from 'vue';
import { Colors } from '@/api';
import BaseModal from './BaseModal.vue';
import SelectedItemsHeader from '../shared/SelectedItemsHeader.vue';
import type { ModalWidth } from '../types';

/**
 * MultiSelectorTemplate - Generic multi-selection modal template
 *
 * Provides a standardized two-pane layout for any entity multi-selection:
 * - Left pane: browsable list of available items
 * - Right pane: selected items with ability to remove
 * - Fully generic - works with any entity type
 *
 * @example
 * ```vue
 * <multi-selector-template
 *   title="Select Items"
 *   :selected-items="selectedItems"
 *   @save="handleSave"
 *   @search="handleSearch"
 * >
 *   <template #left>
 *     <entity-browser />
 *   </template>
 *   <template #right>
 *     <selected-items-list />
 *   </template>
 *   <template #left-footer>
 *     <include-archived-toggle />
 *   </template>
 * </multi-selector-template>
 * ```
 */

interface Props {
  // Display
  title?: string;
  primaryColor?: Colors;
  width?: ModalWidth;
  height?: number; // Height as vh percentage

  // Selection
  selectedItems?: T[];
  maxSelections?: number;

  // Left pane
  leftHeaderTitle?: string;
  leftPaneClasses?: string | string[] | Record<string, boolean>;

  // Right pane
  rightHeaderTitle?: string;
  rightPaneClasses?: string | string[] | Record<string, boolean>;
  showSelectedCount?: boolean;
  showClearInHeader?: boolean;

  // Header controls
  clearable?: boolean;
  clearText?: string;
  clearAriaLabel?: string;
  showFind?: boolean;
  findPlaceholder?: string;
  autoFocusFind?: boolean;
  showSelectAll?: boolean;
  showBadgeCount?: boolean;

  // Header Row 3 Controls
  showArchivedToggle?: boolean;
  includeArchived?: boolean;
  showNoneOption?: boolean;
  noneOptionText?: string;
  noneSelected?: boolean;

  // Row Visibility
  showRow2?: boolean;

  // Footer
  showFooterControls?: boolean;
  showFooter?: boolean;
  primaryActionText?: string;
  secondaryActionText?: string;
  hideSecondaryAction?: boolean;
  showLeftFooter?: boolean;
  showRightFooter?: boolean;

  // State
  loading?: boolean;
}

interface Emits {
  (e: 'show'): void;
  (e: 'hide'): void;
  (e: 'shown'): void;
  (e: 'clear'): void;
  (e: 'search', query: string, immediate?: boolean): void;
  (e: 'select-all', selected: boolean): void;
  (e: 'save'): void;
  (e: 'cancel'): void;
  (e: 'focus-first'): void;
  (e: 'focus-last'): void;
  (e: 'focus-body-last'): void;
  (e: 'archived-toggle-key', event: KeyboardEvent): void;
  (e: 'none-select-key', event: KeyboardEvent): void;
  (e: 'toggle-archived', value: boolean): void;
  (e: 'select-none', value: boolean): void;
  (e: 'toggle-subtasks'): void;
  (e: 'create-key', event: KeyboardEvent): void;
  (e: 'new-key', event: KeyboardEvent): void;
  (e: 'navigate-left'): void;
  (e: 'navigate-right'): void;
}

const props = withDefaults(defineProps<Props>(), {
  primaryColor: Colors.BLUE,
  width: 'xl',
  height: 70, // Default to 70vh
  selectedItems: () => [],
  showFind: true,
  showFooter: true,
  showBadgeCount: true,
  showSelectedCount: true,
  showClearInHeader: false,
  clearable: true,
  showSelectAll: true, // Changed to true for multi-selectors
  autoFocusFind: true,
  hideSecondaryAction: false,
  showLeftFooter: false,
  showRightFooter: false,
  showRow2: true, // Added default to show the second row with search/select-all/badge
});

const emit = defineEmits<Emits>();

// Computed CSS variables for dynamic theming
const cssVars = computed(() => {
  const primaryColor = `rgb(var(--tw-color-${props.primaryColor}))`;
  return {
    '--scrollbar-color': `color-mix(in srgb, ${primaryColor} 80%, white)`,
  };
});

// Refs
// Type for BaseModal exposed methods
interface BaseModalInstance {
  showPopup: (trigger?: HTMLElement) => void;
  closePopup: () => void;
  focusHeader?: () => void;
  isVisible: boolean;
  updateSelectedCount: (count: number) => void;
  updateHasClearableContent: (hasContent: boolean) => void;
  updateSelectAllState?: (state: 'all' | 'some' | 'none') => void;
}

const modalRef = ref<BaseModalInstance>();
const searchQuery = ref('');

// Computed
const selectedCount = computed(() => props.selectedItems?.length || 0);
const hasSelections = computed(() => selectedCount.value > 0);

// Watch for selection changes to update modal state
watch(selectedCount, (count) => {
  modalRef.value?.updateSelectedCount(count);
  modalRef.value?.updateHasClearableContent(count > 0);
});

// Event handlers
function onShow() {
  emit('show');
}

function onHide() {
  emit('hide');
}

function onShown() {
  emit('shown');
  // Update modal state after shown
  modalRef.value?.updateSelectedCount(selectedCount.value);
  modalRef.value?.updateHasClearableContent(hasSelections.value);
}

function handleClear() {
  // Clear search query when clear button is clicked
  searchQuery.value = '';
  emit('clear');
}

function handleSearch(query: string, immediate?: boolean) {
  searchQuery.value = query;
  emit('search', query, immediate);
}

function handleSelectAll(selected: boolean) {
  emit('select-all', selected);
}

function handleSave() {
  emit('save');
}

function handleCancel() {
  emit('cancel');
}

function handleEnterKey() {
  // IMPORTANT: Enter should ONLY save, not toggle items
  // Don't click on list items - just save
  handleSave();
}

// Public API
function showPopup(trigger?: HTMLElement) {
  // Reset search query when opening modal
  searchQuery.value = '';
  modalRef.value?.showPopup(trigger);
}

function closePopup() {
  modalRef.value?.closePopup();
}

function clearSearch() {
  searchQuery.value = '';
}

defineExpose({
  showPopup,
  closePopup,
  clearSearch,
  focusHeader: () => modalRef.value?.focusHeader?.(),
  isVisible: computed(() => modalRef.value?.isVisible || false),
  updateSelectAllState: (state: 'all' | 'some' | 'none') => {
    modalRef.value?.updateSelectAllState?.(state);
  },
});
</script>

<style lang="scss" scoped>
.two-pane-template {
  // No min-height - flex layout handles sizing
  // The parent modal body constrains the height, and flex children
  // with min-height: 0 will shrink properly to fit

  &__left,
  &__right {
    display: flex;
    flex-direction: column;
    overflow: hidden; // Prevent overflow from children
  }

  &__left {
    @apply tw-border-r tw-border-gray-medium-light;
  }

  &__left-header,
  &__right-header {
    @apply tw-bg-gray-light;
    min-height: 36px;
    display: flex;
    align-items: center;
  }

  &__left-body,
  &__right-body {
    flex: 1;
    min-height: 0; // Allow flex item to shrink below content size
    overflow-y: auto;
    overscroll-behavior: contain;

    // Optimize scrolling
    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      @apply tw-bg-gray-medium;
      border-radius: 3px;
    }
  }

  &__left-footer {
    @apply tw-bg-white;
  }

  &__right-footer {
    @apply tw-bg-gray-light;
  }
}

// Empty state styling
.two-pane-template__right-body:has(.tw-items-center.tw-justify-center) {
  display: flex;
}
</style>
